import React from "react";
import { useMetricMeta } from "@client/hooks/useMetricMeta";
import { t } from "@client/lib/dictionary";
import type { BmsTelemetry } from "@client/types/bms";
import { formatMetric, metricLabel } from "@client/utils/formatMetric";
import { Card, CardContent } from "@client/components/ui/card";

interface Props {
  slug: string;
  telemetry: BmsTelemetry | undefined;
}

const Metric: React.FC<{ label: string; value: string; tone?: "good" | "warn" | "bad" }> = ({ label, value, tone }) => {
  const colorClass =
    tone === "good"
      ? "text-green-600"
      : tone === "warn"
        ? "text-amber-600"
        : tone === "bad"
          ? "text-red-600"
          : "text-foreground";
  return (
    <div className="flex flex-col">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`text-2xl font-bold tabular-nums ${colorClass}`}>{value}</span>
    </div>
  );
};

/** Per-kanál label tables (kopie z BE statusEnums.ts — držíme manuálně v sync). */
const CHARGE_LABELS: Record<number, string> = {
  0x00: "OFF",
  0x01: "ON",
  0x02: "OVERCHARGE_PROTECT",
  0x03: "OVER_CURRENT",
  0x04: "FULL",
  0x05: "TOTAL_OVP",
  0x06: "BATT_OVER_TEMP",
  0x07: "MOS_OVER_TEMP",
  0x08: "ABNORMAL_CURRENT",
  0x09: "BALANCE_WIRE_DROP",
  0x0a: "PCB_OVER_TEMP",
  0x0d: "DISCHARGE_MOSFET_FAULT",
  0x0f: "MANUAL_OFF",
};

const DISCHARGE_LABELS: Record<number, string> = {
  0x00: "OFF",
  0x01: "ON",
  0x02: "OVER_DISCHARGE_PROTECT",
  0x03: "OVER_CURRENT",
  0x05: "TOTAL_UVP",
  0x06: "BATT_OVER_TEMP",
  0x07: "MOS_OVER_TEMP",
  0x08: "ABNORMAL_CURRENT",
  0x09: "BATTERY_NOT_DETECTED",
  0x0a: "PCB_OVER_TEMP",
  0x0b: "CHARGE_MOSFET_LOCKOUT",
  0x0c: "SHORT_CIRCUIT",
  0x0d: "DISCHARGE_MOSFET_FAULT",
  0x0e: "START_EXCEPTION",
  0x0f: "MANUAL_OFF",
};

const BALANCER_LABELS: Record<number, string> = {
  0x00: "OFF",
  0x01: "LIMIT_EXCEEDED",
  0x02: "CHARGE_DIFFERENTIAL",
  0x03: "OVER_TEMP",
  0x04: "AUTO_BALANCE",
  0x0a: "PCB_OVER_TEMP",
};

type StatusChannel = "charge" | "discharge" | "balancer";

const labelFor = (channel: StatusChannel, code: number): string => {
  const tables = { charge: CHARGE_LABELS, discharge: DISCHARGE_LABELS, balancer: BALANCER_LABELS };
  return tables[channel][code] ?? `UNKNOWN_0x${code.toString(16).padStart(2, "0")}`;
};

/**
 * Per-kanál fault detection. Balancer má jiný význam kódů než MOSFETy:
 *  - charge/discharge: 0x00 OFF, 0x01 ON, vše ostatní = fault
 *  - balancer: 0x00 OFF, 0x01/0x02/0x04 = aktivní mód, 0x03/0x0a = fault
 */
const isFault = (channel: StatusChannel, code: number): boolean => {
  if (channel === "balancer") return code === 0x03 || code === 0x0a;
  return code !== 0x00 && code !== 0x01;
};

export const BmsOverviewCard: React.FC<Props> = ({ slug, telemetry }) => {
  const topic = (metric: string) => `zige/bms-${slug}/${metric}/val`;
  const socMeta = useMetricMeta(topic("soc"));
  const voltageMeta = useMetricMeta(topic("voltage"));
  const currentMeta = useMetricMeta(topic("current"));
  const powerMeta = useMetricMeta(topic("power"));
  const tempT1Meta = useMetricMeta(topic("temp-t1"));
  const tempT2Meta = useMetricMeta(topic("temp-t2"));
  const tempMosMeta = useMetricMeta(topic("temp-mos"));
  const tempT4Meta = useMetricMeta(topic("temp-t4"));
  const cycleCountMeta = useMetricMeta(topic("cycle-count"));
  const chargeMosfetMeta = useMetricMeta(topic("charge-mosfet"));
  const dischargeMosfetMeta = useMetricMeta(topic("discharge-mosfet"));
  const balancingMeta = useMetricMeta(topic("balancing"));

  if (!telemetry) {
    return (
      <Card>
        <CardContent className="p-6 text-muted-foreground">{t.dashboard.noData}</CardContent>
      </Card>
    );
  }
  const { pack, mosfet } = telemetry;
  const socTone = pack.socPercent < 10 ? "bad" : pack.socPercent < 20 ? "warn" : "good";
  const currentA = pack.packCurrentMa / 1000;
  const isCharging = currentA < -0.1;
  const isDischarging = currentA > 0.1;
  return (
    <Card>
      <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-6 p-6">
        <Metric label={metricLabel(socMeta, t.bms.soc)} value={formatMetric(pack.socPercent, socMeta)} tone={socTone} />
        <Metric
          label={metricLabel(voltageMeta, t.bms.voltage)}
          value={formatMetric(pack.packVoltageMv / 1000, voltageMeta)}
        />
        <Metric
          label={metricLabel(currentMeta, t.bms.current)}
          value={formatMetric(currentA, currentMeta)}
          tone={isCharging ? "good" : isDischarging ? "warn" : undefined}
        />
        <Metric label={metricLabel(powerMeta, t.bms.power)} value={formatMetric(pack.powerMw / 1000, powerMeta)} />
        <Metric
          label={metricLabel(tempT1Meta, t.bms.tempT1)}
          value={formatMetric(pack.temperatureT1DeciC !== null ? pack.temperatureT1DeciC / 10 : null, tempT1Meta)}
        />
        <Metric
          label={metricLabel(tempT2Meta, t.bms.tempT2)}
          value={formatMetric(pack.temperatureT2DeciC !== null ? pack.temperatureT2DeciC / 10 : null, tempT2Meta)}
        />
        <Metric
          label={metricLabel(tempMosMeta, t.bms.tempMos)}
          value={formatMetric(pack.temperatureMosDeciC !== null ? pack.temperatureMosDeciC / 10 : null, tempMosMeta)}
        />
        <Metric
          label={metricLabel(cycleCountMeta, t.bms.cycles)}
          value={formatMetric(pack.cycleCount, cycleCountMeta)}
        />
        {(pack.temperatureT4DeciC !== null || pack.temperatureT5DeciC !== null || pack.temperatureT6DeciC !== null) && (
          <>
            {pack.temperatureT4DeciC !== null && (
              <Metric
                label={metricLabel(tempT4Meta, "T4")}
                value={formatMetric(pack.temperatureT4DeciC / 10, tempT4Meta)}
              />
            )}
            {/* T5/T6 driver dnes nepublikuje na MQTT — zachovat hardcoded label + fmt */}
            {pack.temperatureT5DeciC !== null && (
              <Metric
                label="T5"
                value={formatMetric(
                  pack.temperatureT5DeciC / 10,
                  tempT4Meta ?? { valueType: "FLOAT", decimals: 1, unit: "°C" }
                )}
              />
            )}
            {pack.temperatureT6DeciC !== null && (
              <Metric
                label="T6"
                value={formatMetric(
                  pack.temperatureT6DeciC / 10,
                  tempT4Meta ?? { valueType: "FLOAT", decimals: 1, unit: "°C" }
                )}
              />
            )}
          </>
        )}
        <div className="col-span-2 md:col-span-4 flex flex-wrap gap-2 pt-2 border-t border-border">
          <Badge
            label={metricLabel(chargeMosfetMeta, t.bms.chargeMosfet)}
            channel="charge"
            statusCode={mosfet.chargeStatusCode}
            on={mosfet.chargeOn}
          />
          <Badge
            label={metricLabel(dischargeMosfetMeta, t.bms.dischargeMosfet)}
            channel="discharge"
            statusCode={mosfet.dischargeStatusCode}
            on={mosfet.dischargeOn}
          />
          <Badge
            label={metricLabel(balancingMeta, t.bms.balancer)}
            channel="balancer"
            statusCode={mosfet.balancerStatusCode}
            on={mosfet.balancing}
          />
        </div>
      </CardContent>
    </Card>
  );
};

const Badge: React.FC<{
  label: string;
  channel: StatusChannel;
  statusCode: number;
  on: boolean;
}> = ({ label, channel, statusCode, on }) => {
  const fault = isFault(channel, statusCode);
  const statusText = statusCode === 0x00 ? t.bms.off : statusCode === 0x01 ? t.bms.on : labelFor(channel, statusCode);
  const bgClass = fault
    ? "bg-red-600 text-white"
    : on
      ? "bg-green-600 text-white"
      : "bg-zinc-200 dark:bg-zinc-800 text-muted-foreground";
  return (
    <span
      className={`inline-flex items-center gap-2 rounded px-2.5 py-1 text-xs font-medium ${bgClass}`}
      title={`Status code 0x${statusCode.toString(16).padStart(2, "0")}`}
    >
      <span className="size-1.5 rounded-full bg-current" />
      {label}: {statusText}
    </span>
  );
};
